import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import Modal from "../../Modal";

const BrickwallDonationForm = ({
	config,
	register,
	errors,
	setValue,
	watch,
	trigger,
	onDonate,
	onClearSelection,
	brickPosition,
}) => {
	// Available brick colors
	const colors = [
		{ value: "#00C34B", label: "Green" },
		{ value: "#A8D6FF", label: "Sky Blue" },
		{ value: "#289BFF", label: "Blue" },
		{ value: "#001957", label: "Dark Blue" },
		{ value: "#FFA5DC", label: "Pink" },
		{ value: "#FFC300", label: "Gold" },
	];

	// Get initial values from form if they exist
	const getInitialColor = () => {
		if (watch && watch("brick_colour")) {
			return watch("brick_colour");
		}
		const randomIndex = Math.floor(Math.random() * colors.length);
		return colors[randomIndex].value;
	};

	// Internal state management
	const [donorName, setDonorName] = useState(() => {
		return watch && watch("brick_donor_name") ? watch("brick_donor_name") : "";
	});
	const [donationAmount, setDonationAmount] = useState(() => {
		const amount =
			watch &&
			(watch("brick_amount") || watch("donation_frequency_and_amount[amount]"));
		return amount || 50;
	});
	const [brickColor, setBrickColor] = useState(getInitialColor);
	const [hideNameOnBrick, setHideNameOnBrick] = useState(() => {
		return watch && watch("brickwall_hide_name") == 1;
	});
	const [hideAmountOnBrick, setHideAmountOnBrick] = useState(() => {
		return watch && watch("brickwall_hide_amount") == 1;
	});
	const [showErrorModal, setShowErrorModal] = useState(false);

	// Register hidden amount fields
	useEffect(() => {
		if (register) {
			register("donation_frequency_and_amount[amount]");
			register("brick_amount");
			register("forename");
			register("surname");
			register("brick_donor_name");
			register("brick_colour");
			register("brickwall_hide_name");
			register("brickwall_hide_amount");
		}
	}, [register]);

	// Keep amount fields in sync with the simple donationAmount value
	useEffect(() => {
		if (!setValue) return;
		const num =
			typeof donationAmount === "number"
				? donationAmount
				: parseFloat(donationAmount);
		if (!isNaN(num) && num > 0) {
			setValue("donation_frequency_and_amount[amount]", num);
			setValue("brick_amount", num);
		}
	}, [donationAmount, setValue]);

	// Sync donor name with form
	useEffect(() => {
		if (setValue && donorName) {
			setValue("brick_donor_name", donorName);
		}
	}, [donorName, setValue]);

	// Sync brick color with form
	useEffect(() => {
		if (setValue && brickColor) {
			setValue("brick_colour", brickColor);
		}
	}, [brickColor, setValue]);

	// Sync hide options with form
	useEffect(() => {
		if (setValue) {
			setValue("brickwall_hide_name", hideNameOnBrick ? 1 : 0);
			setValue("brickwall_hide_amount", hideAmountOnBrick ? 1 : 0);
		}
	}, [hideNameOnBrick, hideAmountOnBrick, setValue]);

	const handleAmountChange = (e) => {
		const value = e.target.value;

		// Allow empty string for UX, but coerce to number when possible
		if (value === "") {
			setDonationAmount("");
			if (trigger) {
				trigger(["brick_amount", "donation_frequency_and_amount[amount]"]);
			}
			return;
		}

		const num = parseFloat(value);
		if (isNaN(num)) {
			setDonationAmount("");
			return;
		}

		// Simple positive number constraint
		if (num <= 0) {
			setDonationAmount("");
			if (trigger) {
				trigger(["brick_amount", "donation_frequency_and_amount[amount]"]);
			}
			return;
		}

		setDonationAmount(num);
		if (trigger) {
			trigger(["brick_amount", "donation_frequency_and_amount[amount]"]);
		}
	};

	// Split donor name into forename and surname if it contains a space
	useEffect(() => {
		if (setValue && donorName && donorName.trim()) {
			const trimmedName = donorName.trim();
			// Check if the name contains a space (two separate strings)
			if (trimmedName.includes(" ")) {
				const nameParts = trimmedName.split(/\s+/);
				if (nameParts.length >= 2) {
					// First part is forename, rest is surname
					const forename = nameParts[0];
					const surname = nameParts.slice(1).join(" ");
					setValue("forename", forename);
					setValue("surname", surname);
				}
			}
			// If no space, don't set forename and surname
		}
	}, [donorName, setValue]);

	// Get register props for controlled inputs
	const donorNameRegister = register ? register("brick_donor_name") : {};
	const brickColourRegister = register ? register("brick_colour") : {};
	const hideNameRegister = register ? register("brickwall_hide_name") : {};
	const hideAmountRegister = register ? register("brickwall_hide_amount") : {};

	const amountError =
		errors?.brick_amount ||
		errors?.donation_frequency_and_amount?.amount ||
		null;
	const isAmountValuePresent =
		donationAmount !== "" && !isNaN(Number(donationAmount));

	// Collect all validation errors
	const getAllErrors = () => {
		const errorMessages = [];

		if (!donorName.trim()) {
			errorMessages.push("Please enter your name");
		} else if (donorName.length > 30) {
			errorMessages.push("Name must be 30 characters or less");
		}

		if (amountError) {
			errorMessages.push(amountError.message);
		} else if (!isAmountValuePresent || Number(donationAmount) <= 0) {
			errorMessages.push("Please enter a valid donation amount");
		}

		return errorMessages;
	};

	// Handle input changes - update both local state and form state
	const handleDonorNameChange = (e) => {
		const value = e.target.value;
		// Enforce 30 character limit
		const truncatedValue = value.slice(0, 30);
		setDonorName(truncatedValue);
		if (setValue) {
			setValue("brick_donor_name", truncatedValue);
		}
		// Call react-hook-form's onChange if it exists
		if (donorNameRegister.onChange) {
			donorNameRegister.onChange(e);
		}
	};

	const handleBrickColorChange = (e) => {
		const value = e.target.value;
		setBrickColor(value);
		if (setValue) {
			setValue("brick_colour", value);
		}
		// Call react-hook-form's onChange if it exists
		if (brickColourRegister.onChange) {
			brickColourRegister.onChange(e);
		}
	};

	const handleHideNameChange = (e) => {
		const checked = e.target.checked;
		setHideNameOnBrick(checked);
		if (setValue) {
			setValue("brickwall_hide_name", checked ? 1 : 0);
		}
		if (hideNameRegister.onChange) {
			hideNameRegister.onChange(e);
		}
	};

	const handleHideAmountChange = (e) => {
		const checked = e.target.checked;
		setHideAmountOnBrick(checked);
		if (setValue) {
			setValue("brickwall_hide_amount", checked ? 1 : 0);
		}
		if (hideAmountRegister.onChange) {
			hideAmountRegister.onChange(e);
		}
	};

	const handleDonateClick = async () => {
		// First check basic validation without triggering react-hook-form
		const errorMessages = getAllErrors();
		if (errorMessages.length > 0) {
			setShowErrorModal(true);
			return;
		}

		// Trigger react-hook-form validation
		if (trigger) {
			const fieldsToValidate = [
				"brick_donor_name",
				"brick_amount",
				"donation_frequency_and_amount[amount]",
			];
			const isValid = await trigger(fieldsToValidate);

			// Check errors again after validation
			if (!isValid) {
				const updatedErrors = getAllErrors();
				if (updatedErrors.length > 0) {
					setShowErrorModal(true);
					return;
				}
			}
		}

		// If we get here, validation passed
		if (onDonate) {
			onDonate();
		}
	};

	// Calculate tooltip position above or below the brick based on viewport position
	const getTooltipStyle = () => {
		if (!brickPosition) return {};

		// Check if we're on mobile (viewport width < 600px)
		const isMobile =
			typeof window !== "undefined" ? window.innerWidth < 600 : false;

		// Check if brick is in the top half of the viewport
		const viewportHeight =
			typeof window !== "undefined" ? window.innerHeight : 0;
		const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
		const isInTopHalf = brickPosition.y < viewportHeight / 2;

		// Calculate left position - constrain to viewport bounds to keep tooltip in view
		let leftPosition = isMobile ? "50%" : brickPosition.x;

		// For desktop and tablet, constrain the position to keep tooltip within viewport
		if (!isMobile && viewportWidth > 0 && typeof brickPosition.x === "number") {
			const minLeft = 175; // Half tooltip width + padding
			const maxLeft = viewportWidth - 175;
			leftPosition = Math.max(minLeft, Math.min(brickPosition.x, maxLeft));
		}

		// Calculate arrow position on mobile to point to brick
		let arrowOffset = null;
		if (isMobile && viewportWidth > 0) {
			// Calculate offset from viewport center (where tooltip is centered) to brick position
			const viewportCenterX = viewportWidth / 2;
			const offsetFromCenter = brickPosition.x - viewportCenterX;
			// Convert to percentage offset from center
			const percentageOffset = (offsetFromCenter / viewportWidth) * 100;
			// Constrain to ±35% to keep arrow within tooltip bounds (tooltip is typically 280-400px wide)
			const constrainedOffset = Math.max(-35, Math.min(35, percentageOffset));
			// Arrow position as percentage from left edge of tooltip
			arrowOffset = `${50 + constrainedOffset}%`;
		}

		const baseStyle = {
			position: "fixed",
			left: leftPosition,
			zIndex: 999,
		};

		// Add arrow offset CSS variable on mobile
		if (arrowOffset !== null) {
			baseStyle["--arrow-left"] = arrowOffset;
		}

		if (isInTopHalf) {
			// Position tooltip below the brick
			const bottomPosition =
				brickPosition.bottom && brickPosition.bottom > brickPosition.y
					? brickPosition.bottom
					: brickPosition.y + (brickPosition.height || 0);

			return {
				...baseStyle,
				top: bottomPosition + 10,
				transform: "translate(-50%, 0%)",
				marginTop: "8px",
			};
		} else {
			// Position tooltip above the brick (default)
			return {
				...baseStyle,
				top: brickPosition.y - 10,
				transform: "translate(-50%, -100%)",
				marginBottom: "8px",
			};
		}
	};

	// Determine if tooltip should be positioned below (for arrow direction)
	const isTooltipBelow = () => {
		if (!brickPosition) return false;
		const viewportHeight =
			typeof window !== "undefined" ? window.innerHeight : 0;
		return brickPosition.y < viewportHeight / 2;
	};

	const tooltipStyle = getTooltipStyle();
	const isBelow = isTooltipBelow();

	const formContent = (
		<div
			className={cn("brickwall-donation-tooltip", {
				"brickwall-donation-tooltip--below": isBelow,
			})}
			style={tooltipStyle}
			onClick={(e) => e.stopPropagation()}
		>
			<button
				type="button"
				className="brickwall-donation-tooltip-close"
				onClick={(e) => {
					e.stopPropagation();
					if (onClearSelection) {
						onClearSelection();
					}
				}}
				aria-label="Close"
			>
				×
			</button>
			<h2 className="brickwall-donation-tooltip-title">Customise your brick</h2>

			<div className="brickwall-donation-tooltip-fields">
				<div className="brickwall-donation-tooltip-field">
					<input
						type="text"
						name={donorNameRegister.name || "brick_donor_name"}
						ref={donorNameRegister.ref}
						onBlur={donorNameRegister.onBlur}
						value={donorName}
						onChange={handleDonorNameChange}
						className="brickwall-donation-tooltip-input"
						placeholder="Enter your name"
						maxLength={30}
					/>
					<label className="brickwall-donation-tooltip-checkbox">
						<input
							type="checkbox"
							name={hideNameRegister.name || "brickwall_hide_name"}
							ref={hideNameRegister.ref}
							onBlur={hideNameRegister.onBlur}
							checked={!!hideNameOnBrick}
							onChange={handleHideNameChange}
						/>
						<span>Hide name</span>
					</label>
				</div>

				<div className="brickwall-donation-tooltip-field">
					<div
						className="brickwall-donation-tooltip-select-wrapper"
						style={{
							"--brickwall-selected-color": brickColor || "#d1d5db",
						}}
					>
						<select
							name={brickColourRegister.name || "brick_colour"}
							ref={brickColourRegister.ref}
							onBlur={brickColourRegister.onBlur}
							value={brickColor}
							onChange={handleBrickColorChange}
							className="brickwall-donation-tooltip-select"
						>
							{colors.map((color) => (
								<option key={color.value} value={color.value}>
									{color.label}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="brickwall-donation-tooltip-field">
					<div className="brickwall-donation-tooltip-amount">
						<input
							type="number"
							{...register(`brick_amount`, {
								required: "Please enter an amount",
								min: {
									value: 50,
									message:
										"We appreciate all donations made to The Air Ambulance Service, however we kindly ask for a minimum donation of £" +
										Number(50).toLocaleString("en-GB"),
								},
								max: {
									value: config.max_amount,
									message:
										"We appreciate all donations made to The Air Ambulance Service, however due to processing limitations we aren't able to accept a value more than £" +
										Number(config.max_amount).toLocaleString("en-GB"),
								},
								pattern: {
									value: /^\d+(\.\d{1,2})?$/,
									message:
										"Please enter a valid amount (up to 2 decimal places)",
								},
							})}
							className={cn("brickwall-donation-tooltip-amount-input", {
								"brickwall-donation-tooltip-amount-input--error": !!amountError,
							})}
							min={50}
							max={config.max_amount}
							step="10"
							placeholder="Amount"
							value={donationAmount}
							onChange={handleAmountChange}
						/>
					</div>
					<label className="brickwall-donation-tooltip-checkbox">
						<input
							type="checkbox"
							name={hideAmountRegister.name || "brickwall_hide_amount"}
							ref={hideAmountRegister.ref}
							onBlur={hideAmountRegister.onBlur}
							checked={!!hideAmountOnBrick}
							onChange={handleHideAmountChange}
						/>
						<span>Hide donation amount</span>
					</label>
				</div>

				<button
					type="button"
					onClick={handleDonateClick}
					className="brickwall-donate-btn btn btn--solid btn--lime"
				>
					Donate
				</button>
			</div>

			<Modal
				isOpen={showErrorModal}
				onClose={() => setShowErrorModal(false)}
				title="Please fix the following errors"
				allowClose={true}
			>
				<div className="brickwall-error-modal">
					<ul className="brickwall-error-list">
						{getAllErrors().map((error, index) => (
							<li key={index} className="brickwall-error-item">
								{error}
							</li>
						))}
					</ul>
					<button
						type="button"
						onClick={() => setShowErrorModal(false)}
						className="btn btn--solid btn--lime"
					>
						OK
					</button>
				</div>
			</Modal>
		</div>
	);

	// Render as portal if brickPosition is provided
	if (brickPosition && typeof document !== "undefined") {
		return ReactDOM.createPortal(formContent, document.body);
	}

	return null;
};

export default BrickwallDonationForm;
